/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 80): the call settings "Audio · Video · Settings" of the
 * final design, built from Element Call's own controls.
 */

import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Root as Form } from "@vector-im/compound-web";

import { type Tab } from "../tabs/Tabs";
import { DeviceSelection } from "../settings/DeviceSelection";
import { useMediaDevices } from "../MediaDevicesContext";
import { iosDeviceMenu$ } from "../state/MediaDevices";
import { useUrlParams } from "../UrlParams";
import { useBehavior } from "../useBehavior";
import { Slider } from "../Slider";
import { FieldRow, InputField } from "../input/Input";
import { useTrackProcessor } from "../livekit/TrackProcessorContext";
import {
  backgroundBlur,
  noiseSuppressionSetting,
  soundEffectVolume,
  useSetting,
} from "../settings/settings";
import styles from "../settings/SettingsModal.module.css";
import { pick } from "./callErrorTexts";
import { ionDataSaver } from "./dataSaver";
import { useIonCameraNames } from "./cameraNames";
import {
  CALL_SETTINGS_TEXTS as T,
  ionMirrorSelf,
  ionShowNames,
} from "./callSettings";

/** Background blur; a component of its own, as it needs the processor. */
const IonBlurField: FC<{ language: string }> = ({ language: l }) => {
  const [blur, setBlur] = useSetting(backgroundBlur);
  const { supported } = useTrackProcessor();
  return (
    <FieldRow>
      <InputField
        id="ionBackgroundBlur"
        label={pick(T.blur, l)}
        description={pick(supported ? T.blurDescription : T.blurUnsupported, l)}
        type="checkbox"
        checked={!!blur}
        onChange={(e): void => setBlur(e.target.checked)}
        disabled={!supported}
      />
    </FieldRow>
  );
};

export type IonCallSettingsTab = "audio" | "video" | "preferences";

export function useIonCallSettingsTabs(): Tab<IonCallSettingsTab>[] {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const devices = useMediaDevices();
  const { controlledAudioDevices } = useUrlParams();
  const iosDeviceMenu = useBehavior(iosDeviceMenu$);
  const [volume, setVolume] = useSetting(soundEffectVolume);
  const [volumeRaw, setVolumeRaw] = useState(volume);
  const [mirror, setMirror] = useSetting(ionMirrorSelf);
  const [noiseSuppression, setNoiseSuppression] = useSetting(
    noiseSuppressionSetting,
  );
  const [showNames, setShowNames] = useSetting(ionShowNames);
  const [dataSaver, setDataSaver] = useSetting(ionDataSaver);
  // "Фронтальная" / "Основная" (Figma 08·12)
  const cameras = useIonCameraNames(devices.videoInput, l);

  const audio = (
    <Form>
      {iosDeviceMenu && controlledAudioDevices && (
        <Button
          onClick={(e): void => {
            e.preventDefault();
            window.controls.showNativeAudioDevicePicker?.();
          }}
        >
          {pick(T.changeAudioDevice, l)}
        </Button>
      )}
      {!controlledAudioDevices && (
        <DeviceSelection
          device={devices.audioInput}
          title={pick(T.microphone, l)}
          numberedLabel={(n) =>
            t("settings.devices.microphone_numbered", { n })
          }
        />
      )}
      <DeviceSelection
        device={devices.audioOutput}
        title={pick(T.soundOutput, l)}
        numberedLabel={(n) => t("settings.devices.speaker_numbered", { n })}
      />
      <div className={styles.volumeSlider}>
        <label>
          {pick(T.effectsVolume, l)}
          {": "}
          <span className={styles.settingValue}>
            {Math.round(volumeRaw * 100)}%
          </span>
        </label>
        <p>{pick(T.effectsVolumeDescription, l)}</p>
        <Slider
          label={pick(T.effectsVolume, l)}
          value={volumeRaw}
          onValueChange={setVolumeRaw}
          onValueCommit={setVolume}
          min={0}
          max={1}
          step={0.01}
        />
      </div>
    </Form>
  );

  const video = (
    <Form>
      <DeviceSelection
        device={cameras}
        title={pick(T.camera, l)}
        numberedLabel={(n) => t("settings.devices.camera_numbered", { n })}
      />
      <h4>{pick(T.image, l)}</h4>
      <FieldRow>
        <InputField
          id="ionMirrorSelf"
          label={pick(T.mirror, l)}
          type="checkbox"
          checked={mirror}
          onChange={(e): void => setMirror(e.target.checked)}
        />
      </FieldRow>
      <IonBlurField language={l} />
    </Form>
  );

  const general = (
    <Form>
      <FieldRow>
        <InputField
          id="ionNoiseSuppression"
          label={pick(T.noiseSuppression, l)}
          description={pick(T.noiseSuppressionDescription, l)}
          type="checkbox"
          checked={noiseSuppression}
          onChange={(e): void => setNoiseSuppression(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="ionDataSaver"
          label={pick(T.dataSaver, l)}
          description={pick(T.dataSaverDescription, l)}
          type="checkbox"
          checked={dataSaver}
          onChange={(e): void => setDataSaver(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="ionShowNames"
          label={pick(T.showNames, l)}
          description={pick(T.showNamesDescription, l)}
          type="checkbox"
          checked={showNames}
          onChange={(e): void => setShowNames(e.target.checked)}
        />
      </FieldRow>
    </Form>
  );

  return [
    { key: "audio", name: pick(T.audioTab, l), content: audio },
    { key: "video", name: pick(T.videoTab, l), content: video },
    { key: "preferences", name: pick(T.generalTab, l), content: general },
  ];
}
